import React, { useState, useEffect } from 'react';
import { GraduationCap, School, Target, Globe } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';

interface EducationStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

const ENGLISH_LEVELS = [
  'BEGINNER',
  'ELEMENTARY',
  'INTERMEDIATE',
  'ADVANCED'
];

const NCEA_LEVELS = [
  'NCEA Level 3',
  'University Entrance',
  'Other'
];

// const COUNTRIES = [
//   'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
//   'France', 'Italy', 'Spain', 'Netherlands', 'Sweden', 'Norway', 'Denmark',
//   'Switzerland', 'Austria', 'Belgium', 'Ireland', 'New Zealand', 'Japan',
//   'South Korea', 'Singapore', 'Hong Kong', 'India', 'China', 'Brazil',
//   'Mexico', 'Argentina', 'South Africa', 'Nigeria', 'Ghana', 'Kenya',
// ];

export const EducationStep: React.FC<EducationStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
}) => {
  const [selectedInstitution, setSelectedInstitution] = useState<any>(null);

  useEffect(() => {
    if (formData.universityId) {
      loadInstitutionInfo();
    } else {
      setSelectedInstitution(null);
    }
  }, [formData.universityId]);

  const loadInstitutionInfo = async () => {
    try {
      const institutions = await agentApi.getApprovedInstitutions();
      const institution = institutions.find(inst => inst.id === formData.universityId);
      setSelectedInstitution(institution || null);
    } catch (error) {
      console.error('Error loading institution info:', error);
      setSelectedInstitution(null);
    }
  };

  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const institutionCountry = selectedInstitution?.country || 'New Zealand';
  const highestLevelValue =
    (formData.secondarySchoolHighestLevel &&
      (NCEA_LEVELS.includes(formData.secondarySchoolHighestLevel)
        ? formData.secondarySchoolHighestLevel
        : formData.secondarySchoolHighestLevel.startsWith('Other')
        ? 'Other'
        : formData.secondarySchoolHighestLevel)) ||
    '';
  const otherHighestLevelText =
    formData.secondarySchoolHighestLevel &&
    formData.secondarySchoolHighestLevel.startsWith('Other - ')
      ? formData.secondarySchoolHighestLevel.substring('Other - '.length)
      : '';

  return (
    <div>
      <h5 className="fw-bold mb-3">D. ACADEMIC INFORMATION</h5>

      {/* Secondary School Location */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <School size={18} className="me-2" />
            Secondary School Location
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-2">
              <label className="form-label fw-semibold d-block">
                Overseas or {institutionCountry} (Tick) <span className="text-danger">*</span>
              </label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="secondarySchoolLocation"
                  id="secondarySchoolLocationNZ"
                  value="new-zealand"
                  checked={formData.secondarySchoolLocation === 'new-zealand'}
                  onChange={(e) => handleChange('secondarySchoolLocation', e.target.value)}
                />
                <label className="form-check-label" htmlFor="secondarySchoolLocationNZ">
                  {institutionCountry}
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="secondarySchoolLocation"
                  id="secondarySchoolLocationOverseas"
                  value="overseas"
                  checked={formData.secondarySchoolLocation === 'overseas'}
                  onChange={(e) => handleChange('secondarySchoolLocation', e.target.value)}
                />
                <label className="form-check-label" htmlFor="secondarySchoolLocationOverseas">
                  Overseas
                </label>
              </div>
              {errors.secondarySchoolLocation && (
                <div className="text-danger small mt-1">{errors.secondarySchoolLocation}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Secondary School Information */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <School size={18} className="me-2" />
            Secondary School Information
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                Last Secondary School Attended <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <School size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className={`form-control ${errors.secondarySchoolName ? 'is-invalid' : ''}`}
                  value={formData.secondarySchoolName || ''}
                  onChange={(e) => handleChange('secondarySchoolName', e.target.value)}
                  placeholder="Name of secondary school"
                />
              </div>
              {errors.secondarySchoolName && (
                <div className="invalid-feedback d-block">{errors.secondarySchoolName}</div>
              )}
              {/* <small className="text-muted d-block mt-1">State "Overseas" if not in {institutionCountry}</small> */}
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">
                Last Year at Secondary School <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.secondarySchoolLastYear ? 'is-invalid' : ''}`}
                value={formData.secondarySchoolLastYear || ''}
                onChange={(e) => handleChange('secondarySchoolLastYear', e.target.value)}
                placeholder="e.g., 2023"
              />
              {errors.secondarySchoolLastYear && (
                <div className="invalid-feedback d-block">{errors.secondarySchoolLastYear}</div>
              )}
            </div>

            <div className="col-md-3 mb-3">
              <label className="form-label fw-semibold">
                Highest Level Achieved <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.secondarySchoolHighestLevel ? 'is-invalid' : ''}`}
                value={highestLevelValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'Other') {
                    // Preserve existing custom text if present, otherwise just "Other"
                    if (otherHighestLevelText) {
                      handleChange('secondarySchoolHighestLevel', `Other - ${otherHighestLevelText}`);
                    } else {
                      handleChange('secondarySchoolHighestLevel', 'Other');
                    }
                  } else {
                    handleChange('secondarySchoolHighestLevel', value);
                  }
                }}
              >
                <option value="">Select level</option>
                {NCEA_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
              {errors.secondarySchoolHighestLevel && (
                <div className="invalid-feedback d-block">{errors.secondarySchoolHighestLevel}</div>
              )}
              {highestLevelValue === 'Other' && (
                <input
                  type="text"
                  className={`form-control mt-2 ${errors.secondarySchoolHighestLevel ? 'is-invalid' : ''}`}
                  placeholder="Please specify other highest level achieved"
                  value={otherHighestLevelText}
                  onChange={(e) =>
                    handleChange(
                      'secondarySchoolHighestLevel',
                      e.target.value ? `Other - ${e.target.value}` : 'Other'
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tertiary Education Information */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <GraduationCap size={18} className="me-2" />
            Tertiary Education Information
          </h6>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-12">
              <label className="form-label fw-semibold d-block">
                First year enrolled in tertiary education? <span className="text-danger">*</span>
              </label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="firstYearTertiaryEnrolment"
                  id="firstYearTertiaryEnrolmentYes"
                  checked={formData.firstYearTertiaryEnrolment === true}
                  onChange={() => handleChange('firstYearTertiaryEnrolment', true)}
                />
                <label className="form-check-label" htmlFor="firstYearTertiaryEnrolmentYes">
                  Yes
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="firstYearTertiaryEnrolment"
                  id="firstYearTertiaryEnrolmentNo"
                  checked={formData.firstYearTertiaryEnrolment === false}
                  onChange={() => handleChange('firstYearTertiaryEnrolment', false)}
                />
                <label className="form-check-label" htmlFor="firstYearTertiaryEnrolmentNo">
                  No
                </label>
              </div>
              <small className="text-muted d-block mt-1">
                Will this be the first year you have ever enrolled in a University, Polytechnic, College of Education
                or Private Training Establishment either in {institutionCountry} or overseas since leaving school?
                <br />
                If you answered “No”, please enter the name of the institution you studied at and the year of your
                first enrolment
              </small>
              {errors.firstYearTertiaryEnrolment && (
                <div className="text-danger small mt-1">{errors.firstYearTertiaryEnrolment}</div>
              )}
            </div>
          </div>

          {formData.firstYearTertiaryEnrolment === false && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">Previous Tertiary Institution Name</label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <GraduationCap size={18} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.tertiaryInstitutionName || ''}
                    onChange={(e) => handleChange('tertiaryInstitutionName', e.target.value)}
                    placeholder="Name of previous tertiary institution"
                  />
                </div>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold">Year of First Tertiary Enrolment</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.tertiaryInstitutionYear || ''}
                  onChange={(e) => handleChange('tertiaryInstitutionYear', e.target.value)}
                  placeholder="e.g., 2020"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* English Language Level */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <Globe size={18} className="me-2" />
            English Language Proficiency
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                English Language Level <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={formData.englishLevel || ''}
                onChange={(e) => handleChange('englishLevel', e.target.value)}
              >
                <option value="">Select English level</option>
                {ENGLISH_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
              {errors.englishLevel && (
                <div className="text-danger small mt-1">{errors.englishLevel}</div>
              )}
              <small className="text-muted d-block mt-1">
              What is your current level of English?
              </small>
            </div>
          </div>
          {/* COMMENTED OUT - Test Scores step has been removed */}
          {/* <div className="alert alert-info mt-3 mb-0">
            <small>
              <strong>Note:</strong> Detailed English test scores (IELTS, TOEFL, etc.) should be entered in the "Test Scores" step.
            </small>
          </div> */}
        </div>
      </div>

      {/* Career Intentions */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <Target size={18} className="me-2" />
            Career Intentions
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">
                Career Intentions <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={4}
                value={formData.careerIntentions || ''}
                onChange={(e) => handleChange('careerIntentions', e.target.value)}
                placeholder="Describe your career goals and intentions after completing this programme..."
              />
              {errors.careerIntentions && (
                <div className="text-danger small mt-1">{errors.careerIntentions}</div>
              )}
              <small className="text-muted d-block mt-1">
                Please provide details about your career aspirations and how this programme will help you achieve them
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Legacy Fields - COMMENTED OUT */}
      {/* <div className="alert alert-info">
        <small>
          <strong>Note:</strong> The fields below are legacy fields maintained for compatibility.
        </small>
      </div>

      <div className="card border">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">Legacy Education Fields</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Current Education Level (Legacy)</label>
              <select
                className="form-select"
                value={formData.levelOfEducation || ''}
                onChange={(e) => handleChange('levelOfEducation', e.target.value)}
              >
                <option value="">Select education level</option>
                <option value="High School">High School</option>
                <option value="Associate Degree">Associate Degree</option>
                <option value="Bachelor's Degree">Bachelor's Degree</option>
                <option value="Master's Degree">Master's Degree</option>
                <option value="Doctorate">Doctorate</option>
                <option value="Professional Degree">Professional Degree</option>
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Current Education Details (Legacy)</label>
              <textarea
                className="form-control"
                rows={3}
                value={formData.currentEducation || ''}
                onChange={(e) => handleChange('currentEducation', e.target.value)}
                placeholder="Describe current educational status"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Institution Name (Legacy)</label>
              <input
                type="text"
                className="form-control"
                value={formData.institutionName || ''}
                onChange={(e) => handleChange('institutionName', e.target.value)}
                placeholder="Name of institution"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Institution Country (Legacy)</label>
              <select
                className="form-select"
                value={formData.institutionCountry || ''}
                onChange={(e) => handleChange('institutionCountry', e.target.value)}
              >
                <option value="">Select country</option>
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Degree Name (Legacy)</label>
              <input
                type="text"
                className="form-control"
                value={formData.degreeName || ''}
                onChange={(e) => handleChange('degreeName', e.target.value)}
                placeholder="e.g., Bachelor of Science in Computer Science"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Graduation Date (Legacy)</label>
              <input
                type="date"
                className="form-control"
                value={formData.graduationDate || ''}
                onChange={(e) => handleChange('graduationDate', e.target.value)}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">GPA (Legacy)</label>
              <input
                type="text"
                className="form-control"
                value={formData.gpa || ''}
                onChange={(e) => handleChange('gpa', e.target.value)}
                placeholder="e.g., 3.5"
              />
            </div>

            <div className="col-md-8 mb-3">
              <label className="form-label fw-semibold">Grading Scheme (Legacy)</label>
              <input
                type="text"
                className="form-control"
                value={formData.gradingScheme || ''}
                onChange={(e) => handleChange('gradingScheme', e.target.value)}
                placeholder="e.g., 4.0 scale, Percentage, etc."
              />
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};
